import { usePreCheckinQuestion } from './usePreCheckinQuestion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KioskApptSearchParams } from './useKioskFindAppointment';
import { KioskAppointment } from 'types/appointments';
import { createSubject } from 'utils/subject';

type KioskLoadingState =
  | 'searching_birthdate'
  | 'searching_birthdate_surname_initial'
  | 'searching_phone'
  | 'kiosk_update_user_loading'
  | 'kiosk_checkin_loading';

type KioskGeneralErrorState = 'kiosk_general_error';
export type KioskFindAppointmentErrorState =
  | KioskGeneralErrorState
  | 'kiosk_multiple_results_error'
  | 'kiosk_no_appts_found_error';
export type KioskCheckinErrorState =
  | KioskGeneralErrorState
  | 'kiosk_early_error'
  | 'kiosk_late_error';
export type KioskErrorState =
  | KioskCheckinErrorState
  | KioskFindAppointmentErrorState;

export type KioskBirthdate = { month: number; day: number };

export type KioskBirthDayInputState = {
  name: 'kiosk_birth_day_input';
  month: number;
};
// Asked only when more patients share the birthdate.
export type KioskSurnameInitialInputState = {
  name: 'kiosk_surname_initial_input';
} & KioskBirthdate;

export type KioskState =
  | 'pre-checkin-question'
  | 'kiosk_birth_month_input'
  | KioskBirthDayInputState
  | KioskSurnameInitialInputState
  | 'phone_input'
  | 'kiosk_appt_select'
  | 'kiosk_update_user' // for updating telephone user name for calling screen
  | 'kiosk_checkin_success'
  | KioskErrorState
  | KioskLoadingState;

export const KIOSK_PRE_CHECKIN_QUESTION: KioskState = 'pre-checkin-question';
export const KIOSK_BIRTH_MONTH_INPUT: KioskState = 'kiosk_birth_month_input';
export const kioskBirthDayInput = (month: number): KioskBirthDayInputState => ({
  name: 'kiosk_birth_day_input',
  month,
});
export const kioskSurnameInitialInput = ({
  month,
  day,
}: KioskBirthdate): KioskSurnameInitialInputState => ({
  name: 'kiosk_surname_initial_input',
  month,
  day,
});

const isKioskStateNamed =
  <T extends Exclude<KioskState, string>>(name: T['name']) =>
  (state: KioskState): state is T =>
    typeof state === 'object' && state.name === name;
export const isKioskBirthDayInput = isKioskStateNamed<KioskBirthDayInputState>(
  'kiosk_birth_day_input',
);
export const isKioskSurnameInitialInput =
  isKioskStateNamed<KioskSurnameInitialInputState>(
    'kiosk_surname_initial_input',
  );
export const KIOSK_PHONE_INPUT: KioskState = 'phone_input';
export const KIOSK_EARLY_ERROR: KioskCheckinErrorState = 'kiosk_early_error';
export const KIOSK_LATE_ERROR: KioskCheckinErrorState = 'kiosk_late_error';
export const KIOSK_NO_APPTS_FOUND_ERROR: KioskFindAppointmentErrorState =
  'kiosk_no_appts_found_error';
export const KIOSK_GENERAL_ERROR: KioskGeneralErrorState =
  'kiosk_general_error';
export const KIOSK_MULTIPLE_RESULTS_ERROR: KioskFindAppointmentErrorState =
  'kiosk_multiple_results_error';
export const KIOSK_APPT_SELECT: KioskState = 'kiosk_appt_select';
export const KIOSK_UPDATE_USER: KioskState = 'kiosk_update_user';
export const KIOSK_CHECKIN_SUCCESS: KioskState = 'kiosk_checkin_success';

export const SEARCHING_BIRTHDATE: KioskLoadingState = 'searching_birthdate';
export const SEARCHING_BIRTHDATE_SURNAME_INITIAL: KioskLoadingState =
  'searching_birthdate_surname_initial';
export const SEARCHING_PHONE: KioskLoadingState = 'searching_phone';
export const KIOSK_UPDATE_USER_LOADING: KioskLoadingState =
  'kiosk_update_user_loading';
export const KIOSK_CHECKIN_LOADING: KioskLoadingState = 'kiosk_checkin_loading';
export const KIOSK_LOADING_STATES: KioskState[] = [
  SEARCHING_BIRTHDATE,
  SEARCHING_BIRTHDATE_SURNAME_INITIAL,
  SEARCHING_PHONE,
  KIOSK_UPDATE_USER_LOADING,
  KIOSK_CHECKIN_LOADING,
];

/**
 * Kiosk should reset after 30 seconds of inactivity so the next user coming to kiosk
 * is at the start of the process. This could be for example when previous user does
 * not click exit button on success screen and leaves the kiosk.
 */
const KIOSK_RESET_TIMEOUT = 30_000;

export const useKioskState = () => {
  const { hasPreCheckinQuestion } = usePreCheckinQuestion();

  const initialState = useMemo(
    () =>
      hasPreCheckinQuestion
        ? KIOSK_PRE_CHECKIN_QUESTION
        : KIOSK_BIRTH_MONTH_INPUT,
    [hasPreCheckinQuestion],
  );

  const [kioskState, setKioskState] = useState<KioskState>(initialState);
  const [kioskApptSearchInput, setKioskApptSearchInput] =
    useState<KioskApptSearchParams>({
      searchString: '',
      type: 'BIRTHDATE',
    });
  const [appt, setAppt] = useState<KioskAppointment>();

  const [resetEvent] = useState(() => createSubject<void>());
  const reset = useCallback(() => {
    resetEvent.next();
    setAppt(undefined);
    setKioskState(initialState);
  }, [initialState, resetEvent]);

  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const initOrRestartResetTimer = useCallback(() => {
    clearResetTimer();
    resetTimerRef.current = setTimeout(() => {
      reset();
    }, KIOSK_RESET_TIMEOUT);
  }, [clearResetTimer, reset]);

  // Clear reset timeout on unmount
  useEffect(() => {
    // Start timer on mount
    initOrRestartResetTimer();
    return () => {
      clearResetTimer();
    };
  }, [initOrRestartResetTimer, clearResetTimer]);

  const onUserActivity = useCallback(() => {
    initOrRestartResetTimer();
  }, [initOrRestartResetTimer]);

  return {
    kioskState,
    setKioskState,
    reset,
    resetEvent,
    kioskApptSearchInput,
    setKioskApptSearchInput,
    appt,
    setAppt,
    onUserActivity,
  };
};
